/* Flux PDP shared layout enhancement. Purchasing stays with the native theme. */
(function (global) {
  'use strict';

  if (global.FluxPDPTemplate) {
    global.FluxPDPTemplate.refresh();
    return;
  }

  var ROOT = '[data-flux-pdp]';
  var instances = new Map();
  var pending = false;

  function setHidden(element, hidden) {
    if (element && element.hidden !== hidden) element.hidden = hidden;
  }

  function setAttribute(element, name, value) {
    if (element && element.getAttribute(name) !== value) element.setAttribute(name, value);
  }

  function stringsFor(root) {
    var element = root.querySelector('[data-fp-strings]');
    try { return element ? JSON.parse(element.textContent) : {}; }
    catch (_) { return {}; }
  }

  function movePurchaseSupport(root) {
    var details = root.querySelector('.product-details > .group-block');
    if (!details) return;
    var content = details.querySelector(':scope > .group-block-content') || details;
    var support = root.querySelector('[data-fp-purchase-support]');
    if (support && support.parentElement !== content) content.appendChild(support);
    var shipping = root.querySelector('[data-fp-shipping-line]');
    var price = content.querySelector('product-price');
    if (shipping && price && price.nextElementSibling !== shipping) {
      price.after(shipping);
    }
  }

  function updatePurchaseSupport(root) {
    var support = root.querySelector('[data-fp-purchase-support]');
    if (!support) return;
    var visible = Array.from(support.children).some(function (child) { return !child.hidden; });
    setHidden(support, !visible);
  }

  function buildContent(state) {
    var root = state.root;
    var source = root.querySelector('[data-fp-description]');
    var destination = root.querySelector('[data-fp-content]');
    var highlights = root.querySelector('[data-fp-highlights]');
    var cards = root.querySelector('[data-fp-features-grid]');
    var featuresSection = root.querySelector('[data-fp-features-section]');
    var story = root.querySelector('[data-fp-description-section]');
    if (!source || !destination || !global.FluxPDPContent) return;

    var raw = source.innerHTML;
    var changed = source !== state.source || destination !== state.destination ||
      raw !== state.raw || !['true', 'fallback'].includes(destination.dataset.fpEnhanced) ||
      highlights !== state.highlights || cards !== state.cards;
    if (!changed) return;

    try {
      // Keep anchored rich descriptions in their original DOM. Cloning IDs or
      // named anchors would send in-page links into the hidden server copy.
      if (source.querySelector('[id],a[name]')) {
        setHidden(source, false);
        setHidden(destination, true);
        setHidden(story, false);
        setHidden(highlights, true);
        setHidden(featuresSection, true);
        destination.dataset.fpEnhanced = 'fallback';
        state.source = source;
        state.destination = destination;
        state.highlights = highlights;
        state.cards = cards;
        state.raw = raw;
        return;
      }
      var parsed = global.FluxPDPContent.parse(raw);
      // Build everything off-document before hiding the readable server copy.
      var description = document.createElement('div');
      description.innerHTML = parsed.detailHtml;
      var blocks = Array.from(description.children);
      var plainParagraphs = blocks.length > 1 && blocks.every(function (block) { return block.tagName === 'P'; });
      var hasLooseText = Array.from(description.childNodes).some(function (node) {
        return node.nodeType === 3 && node.textContent.trim();
      });
      if (plainParagraphs && !hasLooseText && description.textContent.length > 900) {
        var full = document.createElement('details');
        full.className = 'full-description';
        var summary = document.createElement('summary');
        summary.textContent = stringsFor(root).read_full_description || 'Read full description';
        full.appendChild(summary);
        blocks.slice(1).forEach(function (block) { full.appendChild(block); });
        description.appendChild(full);
      }

      var quickFragment = document.createDocumentFragment();
      var cardFragment = document.createDocumentFragment();
      // If a theme block has been removed in the editor, retain its content below.
      var quickCount = highlights ? Math.min(3, parsed.features.length) : 0;
      parsed.features.slice(0, quickCount).forEach(function (text) {
        var item = document.createElement('div');
        item.textContent = text;
        quickFragment.appendChild(item);
      });
      var remaining = parsed.features.slice(quickCount);
      if (cards) {
        remaining.forEach(function (text) {
          var card = document.createElement('article');
          card.className = 'feature-card';
          var paragraph = document.createElement('p');
          paragraph.textContent = text;
          card.appendChild(paragraph);
          cardFragment.appendChild(card);
        });
      } else if (remaining.length) {
        var list = document.createElement('ul');
        remaining.forEach(function (text) {
          var item = document.createElement('li');
          item.textContent = text;
          list.appendChild(item);
        });
        description.appendChild(list);
      }

      var meaningful = Boolean(description.textContent.trim() || description.querySelector('img,video,audio,picture,table,hr'));
      destination.replaceChildren.apply(destination, Array.from(description.childNodes));
      if (highlights) {
        highlights.replaceChildren(quickFragment);
        setHidden(highlights, quickCount === 0);
      }
      if (cards) cards.replaceChildren(cardFragment);
      setHidden(featuresSection, !cards || !remaining.length);
      setHidden(destination, !meaningful);
      setHidden(source, true);
      setHidden(story, !meaningful);
      setHidden(root.querySelector('.details-link'), !meaningful);
      destination.dataset.fpEnhanced = 'true';
      state.source = source;
      state.destination = destination;
      state.highlights = highlights;
      state.cards = cards;
      state.raw = raw;
    } catch (_) {
      // A parser failure must leave the source product description readable.
      setHidden(source, false);
      setHidden(destination, true);
      setHidden(story, false);
      setHidden(highlights, true);
      setHidden(featuresSection, true);
    }
  }

  function galleryState(state, gallery) {
    var slides = Array.from(gallery.querySelectorAll('[data-fp-slide]'));
    var current = state.galleries.get(gallery);
    if (!current || current.slides.length !== slides.length || current.slides.some(function (slide, i) { return slide !== slides[i]; })) {
      var visible = slides.findIndex(function (slide) { return !slide.hidden; });
      current = { slides: slides, index: Math.max(0, visible) };
      state.galleries.set(gallery, current);
    }
    return current;
  }

  function showSlide(state, gallery, index, userInitiated) {
    var current = galleryState(state, gallery);
    var total = current.slides.length;
    if (!total) return;
    current.index = (index + total) % total;
    current.slides.forEach(function (slide, i) {
      var hidden = i !== current.index;
      if (hidden && !slide.hidden) slide.querySelectorAll('video,audio').forEach(function (media) { media.pause(); });
      setHidden(slide, hidden);
    });
    gallery.querySelectorAll('[data-fp-thumb]').forEach(function (button) {
      var selected = Number(button.dataset.fpThumb) === current.index;
      setAttribute(button, 'aria-pressed', String(selected));
      if (selected && userInitiated) {
        // Scroll only the thumbnail strip; scrollIntoView can move the whole page.
        var strip = button.parentElement;
        if (strip && strip.scrollWidth > strip.clientWidth) {
          var left = button.offsetLeft - strip.offsetLeft;
          if (left < strip.scrollLeft) strip.scrollLeft = left;
          else if (left + button.offsetWidth > strip.scrollLeft + strip.clientWidth) {
            strip.scrollLeft = left + button.offsetWidth - strip.clientWidth;
          }
        }
      }
    });
    var counter = gallery.querySelector('[data-fp-count]');
    var pattern = stringsFor(state.root).image_counter || '{current} / {total}';
    var label = pattern.replace(/\{\{?\s*current\s*\}\}?/g, String(current.index + 1)).replace(/\{\{?\s*total\s*\}\}?/g, String(total));
    if (counter && counter.textContent !== label) counter.textContent = label;
    setHidden(gallery.querySelector('[data-fp-prev]'), total < 2);
    setHidden(gallery.querySelector('[data-fp-next]'), total < 2);
  }

  function selectVariantSlide(state, variant) {
    if (!variant) return;
    var variantId = String(variant.id || "").split("/").pop();
    var featuredMedia = variant.featured_media || variant.featuredMedia;
    var featuredMediaId = featuredMedia && String(featuredMedia.id || "").split("/").pop();
    var featuredImage = variant.featured_image || variant.featuredImage;
    var featuredImageId = featuredImage && String(featuredImage.id || "").split("/").pop();
    state.root.querySelectorAll("[data-fp-gallery]").forEach(function (gallery) {
      var slides = Array.from(gallery.querySelectorAll("[data-fp-slide]"));
      var index = slides.findIndex(function (slide) {
        return (slide.dataset.fpVariantIds || "").split(",").some(function (id) { return String(id).trim().split("/").pop() === variantId; });
      });
      if (index < 0 && (featuredMediaId || featuredImageId)) index = slides.findIndex(function (slide) { return slide.dataset.fpMediaId === featuredMediaId || slide.dataset.fpImageId === featuredImageId; });
      if (index >= 0) showSlide(state, gallery, index, true);
    });
  }

  function createState(root) {
    var state = { root: root, galleries: new WeakMap(), abort: new AbortController(), touch: null };
    var options = { signal: state.abort.signal };
    root.addEventListener('click', function (event) {
      if (!(event.target instanceof Element)) return;
      var button = event.target.closest('[data-fp-thumb],[data-fp-prev],[data-fp-next]');
      var gallery = button && button.closest('[data-fp-gallery]');
      if (!gallery || !root.contains(gallery)) return;
      var current = galleryState(state, gallery);
      var index = button.hasAttribute('data-fp-thumb') ? Number(button.dataset.fpThumb) :
        current.index + (button.hasAttribute('data-fp-prev') ? -1 : 1);
      if (Number.isFinite(index)) showSlide(state, gallery, index, true);
    }, options);
    root.addEventListener('keydown', function (event) {
      if (!(event.target instanceof Element) || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      var gallery = event.target.closest('[data-fp-gallery]');
      if (!gallery || event.target.closest('input,select,textarea,video,audio,[contenteditable]')) return;
      event.preventDefault();
      showSlide(state, gallery, galleryState(state, gallery).index + (event.key === 'ArrowRight' ? 1 : -1), true);
    }, options);
    root.addEventListener('touchstart', function (event) {
      if (!(event.target instanceof Element) || event.touches.length !== 1) return;
      var slide = event.target.closest('[data-fp-slide]');
      var gallery = slide && slide.closest('[data-fp-gallery]');
      state.touch = gallery ? { gallery: gallery, x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
    }, { passive: true, signal: state.abort.signal });
    root.addEventListener('touchend', function (event) {
      var start = state.touch;
      state.touch = null;
      if (!start || !event.changedTouches.length || event.touches.length) return;
      var x = event.changedTouches[0].clientX - start.x;
      var y = event.changedTouches[0].clientY - start.y;
      if (Math.abs(x) > 45 && Math.abs(x) > Math.abs(y) * 1.5) {
        showSlide(state, start.gallery, galleryState(state, start.gallery).index + (x < 0 ? 1 : -1), true);
      }
    }, { passive: true, signal: state.abort.signal });
    root.addEventListener('touchcancel', function () { state.touch = null; }, options);
    state.unsubscribeVariantChange = null;
    if (String(root.dataset.productId) === "10316029755555" && typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      state.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var payload = event && event.data;
        if (!payload || String(payload.sectionId) !== String(root.dataset.section)) return;
        selectVariantSlide(state, payload.variant);
      });
    }
    return state;
  }

  function reconcile() {
    pending = false;
    instances.forEach(function (state, root) {
      if (!root.isConnected || !root.matches(ROOT)) {
        state.abort.abort();
        if (state.unsubscribeVariantChange) state.unsubscribeVariantChange();
        instances.delete(root);
      }
    });
    document.querySelectorAll(ROOT).forEach(function (root) {
      var state = instances.get(root);
      if (!state) {
        state = createState(root);
        instances.set(root, state);
      }
      movePurchaseSupport(root);
      buildContent(state);
      updatePurchaseSupport(root);
      root.querySelectorAll('[data-fp-gallery]').forEach(function (gallery) {
        showSlide(state, gallery, galleryState(state, gallery).index, false);
      });
      setAttribute(root, 'data-fp-ready', 'true');
    });
  }

  function refresh() {
    if (pending) return;
    pending = true;
    global.requestAnimationFrame(reconcile);
  }

  global.FluxPDPTemplate = { refresh: refresh };
  function start() {
    reconcile();
    // Horizon can morph a section or the entire main element in place. The server
    // description remains untouched, so a replacement can safely be enhanced again.
    new MutationObserver(function (records) {
      var relevant = records.some(function (record) {
        if (record.target instanceof Element && record.target.closest(ROOT)) return true;
        return Array.from(record.addedNodes).concat(Array.from(record.removedNodes)).some(function (node) {
          return node instanceof Element && (node.matches(ROOT) || node.querySelector(ROOT));
        });
      });
      if (relevant) refresh();
    }).observe(document.body, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', refresh);
    document.addEventListener('shopify:section:unload', refresh);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})(window);
